import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    TextField,
    Button,
    Typography,
    Paper,
    MenuItem,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit
} from '@mui/icons-material';
import styles from './ChecklistSetting.module.scss';

interface Checklist {
    id: number;
    label: string;
    category: string;
    owner: string;
    workplace?: { id: number; name: string } | null;
    organization?: { id: number; name: string } | null;
    frequency?: string;
    signoff_required?: boolean;
    tasks?: any[];
    templateUid?: string | null;
    version?: number;
    isScheduled?: boolean;
    type?: string | null;
}

  interface ChecklistTemplatesProps {
    sortedChecklists: Checklist[];
    loading: boolean;
  
    handleAddChecklist: () => void;
    handleEditChecklist: (checklist: Checklist) => void;
    handleDeleteChecklistClick: (id: number) => void;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
  }

  export const ChecklistTemplates: React.FC<ChecklistTemplatesProps> = ({
    sortedChecklists,
    loading,
    handleAddChecklist,
    handleEditChecklist,
    handleDeleteChecklistClick,
    canEdit = false,
    canDelete = false,
    canExecute = false,
  }) => {
    return (
        <div className={styles.consistentPanel}>
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
            }}
        >
            <Typography variant="h6">Checklist Templates</Typography>
            <Button
                sx={{
                    backgroundColor: canExecute ? 'rgb(68, 99, 136)' : 'rgba(68, 99, 136, 0.6)',
                    color: 'white',
                    padding: '5px 15px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: canExecute ? 'pointer' : 'not-allowed',
                    textTransform: 'capitalize',
                    '&.Mui-disabled': {
                        color: 'white',
                        opacity: 0.6,
                    },
                    '&:hover': {
                        backgroundColor: canExecute ? 'rgb(58, 85, 117)' : 'rgba(68, 99, 136, 0.6)',
                    },
                }}
                onClick={() => {
                    if (!canExecute) {
                        return;
                    }
                    handleAddChecklist();
                }}
                disabled={!canExecute}
                title={canExecute ? 'Add checklist' : 'You do not have permission to add'}
            >
                Add
            </Button>
        </Box>
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={20} color="inherit" />
            </Box>
        ) : sortedChecklists.length > 0 ? (
            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Checklist Name</th>
                            <th>Category Name</th>
                            <th>Owner</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedChecklists.map((checklist) => (
                            <tr key={checklist.id}>
                                <td>{checklist.label}</td>
                                <td>{checklist.category}</td>
                                <td>{
                                    checklist?.type === 'workplace' ? 'Workplace' :
                                    checklist?.type === 'organization' ? 'Organization' :
                                    checklist?.workplace ? 'Workplace' :
                                    checklist?.organization ? 'Organization' :
                                    checklist.owner
                                }</td>
                                <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                    <button
                                        className={styles.iconButton}
                                        onClick={() => handleEditChecklist(checklist)}
                                        title={canEdit ? 'Edit' : 'You do not have permission to edit'}
                                        disabled={!canEdit}
                                        style={{
                                            opacity: canEdit ? 1 : 0.5,
                                            cursor: canEdit ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        <Edit className={styles.editIcon} />
                                    </button>
                                </td>
                                <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                    <button
                                        className={styles.iconButton}
                                        onClick={() => handleDeleteChecklistClick(checklist.id)}
                                        title={canDelete ? 'Delete' : 'You do not have permission to delete'}
                                        disabled={!canDelete}
                                        style={{
                                            opacity: canDelete ? 1 : 0.5,
                                            cursor: canDelete ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        <DeleteIcon className={styles.deleteIcon} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    No records found.
                </Typography>
            </Box>
        )}
    </div>
    )
  }
